import {
  Box,
  Button,
  DialogContent,
  Grid,
  SelectChangeEvent,
  TextField,
  Select,
  MenuItem,
  Chip,
  Divider,
  Dialog,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import {
  fetchDocumentSettingById,
  addDocumentSetting,
  updateDocumentSetting,
} from "../../store/documentSettingStore/documentSettingThunk";
import { fetchDocumentTypes } from "../../store/documentTypeStore/documentTypeThunk";
import { fetchTemplates } from '../../store/invoiceStore/invoiceThunk';
import { IOptions } from "../FormFields/CustomSelect";
import { FormProvider, useForm } from "react-hook-form";
import { IDocumentSettingFormInputs } from "../../utils/documentSetting.types";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ApiStatus } from "../../utils/api.types";
import LoadingButton from "@mui/lab/LoadingButton";

interface DialogDocumentSettingFormProps {
  title?: string;
  documentSettingId?: string;
  accountId?: string;
  open: boolean;
  onClose: (k: any) => void;
  setDocumentType?: string;
}

const DialogDocumentSettingForm: FC<DialogDocumentSettingFormProps> = ({
  title,
  onClose,
  documentSettingId,
  accountId,
  open,
  setDocumentType
}) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const [documentTypeOptions, setDocumentTypeOptions] = useState<IOptions[]>([]);
  const [documentTypeValue, setDocumentTypeValue] = useState("");

  const [templateOptions, setTemplateOptions] = useState<IOptions[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [documentSettingData, setDocumentSettingData] = useState<any>();
  const [numberLengthValue, setNumberLengthValue] = useState<any>();
  const [sequenceValue, setSequenceValue] = useState<any>();

  // Schema to validate account form.
  const schema = yup.object().shape({
    documentType: yup.string().required("Document type is required."),
    docNumberPrefix: yup.string(),
    footer1: yup.string().required("Footer 1 length is required."),
    footer2: yup.string().required("Footer 2 length is required."),
    docNumberLength: yup
      .number()
      .typeError('Document length is required and should be a number.')
      .required("Document length is required."),
    docSequenceNumber: yup.number()
      .typeError('Sequence number is required.')
      .required("Sequence number is required."),
  });

  //form
  const methods = useForm<IDocumentSettingFormInputs>({
    mode: "onTouched",
    resolver: yupResolver(schema),
  });

  const { status } = useAppSelector((state) => state.documentSettings);

  useEffect(() => {
    const getDocumentSettingDetails = async () => {
      if (documentSettingId) {
        const bd: any = await dispatch(
          fetchDocumentSettingById(documentSettingId)
        );

        setDocumentSettingData(bd.payload);
        setNumberLengthValue(bd.payload.docNumberLength);
        setSequenceValue(bd.payload.docSequenceNumber);

        methods.setValue("docNumberPrefix", bd.payload.docNumberPrefix);
        methods.setValue("docSequenceNumber", bd.payload.docSequenceNumber);
        methods.setValue("docNumberLength", bd.payload.docNumberLength);
        methods.setValue("termsAndConditions", bd.payload.termsAndConditions);
        methods.setValue("paymentDetails", bd.payload.paymentDetails);
        methods.setValue("paymentTermDays", bd.payload.paymentTermDays);
        methods.setValue("notes", bd.payload.notes);
        methods.setValue("footer1", bd.payload.footer1);
        methods.setValue("footer2", bd.payload.footer2);
        methods.setValue("smsAndWhatsAppText", bd.payload.smsAndWhatsAppText);
        methods.setValue("emailSubject", bd.payload.emailSubject);
        methods.setValue("emailText", bd.payload.emailText);
        methods.setValue("documentType", bd.payload.documentType?._id || "");
        setDocumentTypeValue(bd.payload.documentType?._id || "");
        methods.setValue("defaultPdfTemplate", bd.payload.defaultPdfTemplate);
        setSelectedTemplate(bd.payload.defaultPdfTemplate?._id || "");
      } else {
        methods.setValue("footer1", "Powered by wareozo");
        methods.setValue("footer2", "Powered by wareozo");
        methods.setValue("paymentDetails", "Net-30");
      }
    };

    getDocumentSettingDetails();
  }, [documentSettingId, dispatch, methods]);

  useEffect(() => {
    const getDocumentTypes = async () => {
      if (!documentTypeOptions.length) {
        const data: any = await dispatch(fetchDocumentTypes());

        if (setDocumentType && !documentSettingId) {
          let obj = data.payload.find((o: { name: string; }) => o.name.toLowerCase() === setDocumentType);
          console.log(obj._id);
          setDocumentTypeValue(obj._id);
        }

        setDocumentTypeOptions(
          data.payload.map((c: any) => {
            return { label: c.name, value: c._id };
          })
        );
      }
    };
    const getTemplates = async () => {
      if (!templateOptions.length) {
        const data: any = await dispatch(fetchTemplates());

        if (data.payload.length > 0) {
          setTemplateOptions(
            data.payload.map((c: any) => {
              return { label: c.template, value: c._id };
            })
          );
        }
      }
    };

    if (open) {
      getDocumentTypes();
      getTemplates();
    }
  }, [open, dispatch, documentTypeOptions, templateOptions, documentSettingId, setDocumentType]);

  const updateDocumentSettingDetails = async (e: any) => {
    if (documentSettingId) {
      e.account = documentSettingData?.account || '';

      const resData = await dispatch(
        updateDocumentSetting({
          documentSettingPayload: e,
          id: documentSettingId,
        })
      );

      const resMsg = resData.payload?.message?.toUpperCase();
      if (resMsg && resMsg.includes('SUCCESS')) {
        onClose(true);
      }
    }
  };

  const createDocumentSettingDetails = async (e: any) => {
    let _accountId = params.accountId || accountId;
    const resData = await dispatch(
      addDocumentSetting({ ...e, ...{ account: _accountId } })
    );

    if (status === ApiStatus.SUCCESS) {
      onClose(true);
    }
  };

  const onSubmit = (e: any) => {
    e.docSequenceNumber = sequenceValue;

    if (documentSettingId) {
      updateDocumentSettingDetails(e);
      return;
    }

    createDocumentSettingDetails(e);
  };

  const handleSequenceOnChange = (ev: any) => {
    let dVal = '';
    if (numberLengthValue < ev.target.value.length) {
      let temp = ev.target.value.length - numberLengthValue;
      let temp2 = ev.target.value.substring(temp);
      setSequenceValue(temp2);
      dVal = temp2;
    } else {
      setSequenceValue(ev.target.value);
      dVal = ev.target.value;
    }

    methods.setValue("docSequenceNumber", dVal, { shouldValidate: true });
  };

  const handleSequenceOnBlur = (ev: any) => {
    if (numberLengthValue >= ev.target.value.length) {
      let newSequenceValue = String(1).padStart(
        numberLengthValue,
        "0"
      );
      setSequenceValue(newSequenceValue);

      methods.setValue("docSequenceNumber", newSequenceValue, { shouldValidate: true });
    }
  };

  const handleNumberLengthChange = (e: any) => {
    setNumberLengthValue(e.target.value);
    setSequenceValue("");
    methods.setValue("docNumberLength", e.target.value, { shouldValidate: true });
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '1000px',
          height: '98vh',
          maxHeight: '998px',
          margin: '20px 0',
        }
      }}
    >
      <FormProvider {...methods}>
        {(!documentSettingId || (documentSettingId && documentSettingData)) && (
          <Box
            component="form"
            onSubmit={methods.handleSubmit(onSubmit)}
            noValidate
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Grid container>
              <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 2, bgcolor: '#e9ecf1' }} style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }} >
                <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
                  <LoadingButton
                    type="submit"
                    name={'submit'}
                    // disabled={!methods.formState.isValid}
                    loading={status === ApiStatus.LOADING}
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                    variant="contained"
                    size='small'>
                    {`Save`}
                  </LoadingButton>

                  <Button
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                    variant="contained"
                    size='small'
                    onClick={() => {
                      methods.reset();
                      onClose(false);
                    }}>
                    Cancel
                  </Button>
                </div>
                <h3 style={{ marginTop: -2 }}>{documentSettingId ? "Document Settings Update" : "Add Document Settings"}</h3>
              </Grid>
            </Grid>

            <DialogContent sx={{ flex: 1, overflow: "auto", p: 3 }}>
              <Grid container spacing={2} justifyContent="left">
                {/* Basic Info and Footer Settings Row */}
                <Grid item xs={12} mt={1}>
                  <Grid container spacing={2}>
                    {/* Basic Settings Section - Left Side */}
                    <Grid item xs={12} md={8}>
                      <Divider style={{ marginRight: -8 }}>
                        <Chip label="Basic Settings" size="small" />
                      </Divider>

                      <Grid container spacing={2} mt={1}>
                        {/* Column 1 - Document Type, Number Prefix */}
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <h5 className='c-label'>Document Type</h5>
                              {documentTypeOptions?.length && (
                                <Select
                                  className='c-text-field-4'
                                  {...methods.register('documentType')}
                                  disableUnderline
                                  required
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        border: "1.5px solid #e9ecf1",
                                        boxShadow: 'none',
                                        borderRadius: '5px'
                                      }
                                    }
                                  }}
                                  style={{ width: '100%' }}
                                  value={documentTypeValue ? documentTypeValue : ''}
                                  disabled={documentSettingId ? true : false}
                                  onChange={(event: SelectChangeEvent<any>) => {
                                    methods.setValue("documentType", event.target.value, { shouldValidate: true });
                                    setDocumentTypeValue(event.target.value);
                                  }}
                                  size='small'
                                  variant='outlined'
                                  sx={{
                                    boxShadow: "none",
                                    textAlign: 'left',
                                    '& .MuiOutlinedInput-input': { p: 0.5 },
                                    marginBottom: '3px',
                                    ".MuiOutlinedInput-notchedOutline": { border: 0 },
                                    "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
                                    "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
                                  }}
                                >
                                  {documentTypeOptions && documentTypeOptions.map((option) =>
                                    <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                                  )}
                                </Select>
                              )}
                              <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.documentType?.message ?? ""}</p>
                            </Grid>

                            <Grid item xs={12}>
                              <h5 className='c-label'>Document Prefix</h5>
                              <TextField
                                placeholder='Number Prefix'
                                className='c-text-field-4'
                                {...methods.register('docNumberPrefix')}
                                variant="standard"
                                sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                                size='small'
                                InputProps={{
                                  disableUnderline: true,
                                  readOnly: (documentSettingId && !open) ? true : false
                                }}
                              />
                              <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.docNumberPrefix?.message ?? ""}</p>
                            </Grid>

                            <Grid item xs={12}>
                              <h5 className='c-label'>Sequence Number</h5>
                              <TextField
                                type="number"
                                placeholder='Sequence Number'
                                className='c-text-field-4'
                                {...methods.register('docSequenceNumber')}
                                variant="standard"
                                sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                                required
                                size='small'
                                value={sequenceValue}
                                onChange={handleSequenceOnChange}
                                InputProps={{
                                  disableUnderline: true,
                                  readOnly: (documentSettingId && !open) ? true : false
                                }}
                              />
                              <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.docSequenceNumber?.message ?? ""}</p>
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Column 2 - Number Length, Template */}
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <h5 className='c-label'>Number Length</h5>
                              <TextField
                                type="number"
                                placeholder='Number Length'
                                className='c-text-field-4'
                                {...methods.register('docNumberLength')}
                                variant="standard"
                                sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                                required
                                size='small'
                                value={numberLengthValue}
                                onChange={handleNumberLengthChange}
                                onBlur={handleSequenceOnBlur}
                                InputProps={{
                                  disableUnderline: true,
                                  readOnly: (documentSettingId && !open) ? true : false
                                }}
                              />
                              <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.docNumberLength?.message ?? ""}</p>
                            </Grid>

                            <Grid item xs={12}>
                              <h5 className='c-label'>Default PDF Template</h5>
                              {templateOptions?.length && (
                                <Select
                                  className='c-text-field-4'
                                  {...methods.register('defaultPdfTemplate')}
                                  disableUnderline
                                  required
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        border: "1.5px solid #e9ecf1",
                                        boxShadow: 'none',
                                        borderRadius: '5px'
                                      }
                                    }
                                  }}
                                  style={{ width: '100%' }}
                                  value={selectedTemplate ? selectedTemplate : ''}
                                  onChange={(event: SelectChangeEvent<any>) => {
                                    methods.setValue("defaultPdfTemplate", event.target.value, { shouldValidate: true });
                                    setSelectedTemplate(event.target.value);
                                  }}
                                  size='small'
                                  variant='outlined'
                                  sx={{
                                    boxShadow: "none",
                                    textAlign: 'left',
                                    '& .MuiOutlinedInput-input': { p: 0.5 },
                                    marginBottom: '3px',
                                    ".MuiOutlinedInput-notchedOutline": { border: 0 },
                                    "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
                                    "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
                                  }}
                                >
                                  {templateOptions && templateOptions.map((option) =>
                                    <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                                  )}
                                </Select>
                              )}
                              <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.defaultPdfTemplate?.message ?? ""}</p>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Payment Settings below Basic Settings fields */}
                      <Divider style={{ marginRight: -8, marginTop: 16 }}>
                        <Chip label="Payment Settings" size="small" />
                      </Divider>

                      <Grid container spacing={2} mt={1}>
                        <Grid item xs={6}>
                          <h5 className='c-label'>Payment Term</h5>
                          <TextField
                            placeholder='Payment Term'
                            className='c-text-field-4'
                            {...methods.register('paymentDetails')}
                            variant="standard"
                            sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                            size='small'
                            InputProps={{
                              disableUnderline: true,
                              readOnly: (documentSettingId && !open) ? true : false
                            }}
                          />
                          <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.paymentDetails?.message ?? ""}</p>
                        </Grid>

                        <Grid item xs={6}>
                          <h5 className='c-label'>Payment Term/Expiry Days</h5>
                          <TextField
                            type="number"
                            placeholder='Payment Term/Expiry Days'
                            className='c-text-field-4'
                            {...methods.register('paymentTermDays')}
                            variant="standard"
                            sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                            size='small'
                            InputProps={{
                              disableUnderline: true,
                              readOnly: (documentSettingId && !open) ? true : false
                            }}
                          />
                          <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.paymentTermDays?.message ?? ""}</p>
                        </Grid>
                      </Grid>

                    </Grid>

                    {/* Footer Settings Section - Right Side */}
                    <Grid item xs={12} md={4}>
                      <Divider style={{ marginRight: -8 }}>
                        <Chip label="Footer Settings" size="small" />
                      </Divider>

                      <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                          <h5 className='c-label'>Footer 1</h5>
                          <textarea
                            placeholder='Footer 1'
                            className='c-text-field-4'
                            {...methods.register('footer1')}
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '8px',
                              borderRadius: '8px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                            }}
                            readOnly={(documentSettingId && !open) ? true : false}
                          />
                          <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.footer1?.message ?? ""}</p>
                        </Grid>

                        <Grid item xs={12}>
                          <h5 className='c-label'>Footer 2</h5>
                          <textarea
                            placeholder='Footer 2'
                            className='c-text-field-4'
                            {...methods.register('footer2')}
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '8px',
                              borderRadius: '8px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                            }}
                            readOnly={(documentSettingId && !open) ? true : false}
                          />
                          <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.footer2?.message ?? ""}</p>
                        </Grid>

                        <Grid item xs={12}>
                          <h5 className='c-label'>SMS & WhatsApp Text</h5>
                          <textarea
                            placeholder='SMS & WhatsApp Text'
                            className='c-text-field-4'
                            {...methods.register('smsAndWhatsAppText')}
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '8px',
                              borderRadius: '8px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                            }}
                            readOnly={(documentSettingId && !open) ? true : false}
                          />
                          <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.smsAndWhatsAppText?.message ?? ""}</p>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Communication Settings and Additional Settings Row */}
                <Grid item xs={12} mt={2}>
                  <Grid container spacing={2}>
                    {/* Communication Settings Section - Left Side */}
                    <Grid item xs={12} md={6}>
                      <Divider style={{ marginRight: -8 }}>
                        <Chip label="Communication Settings" size="small" />
                      </Divider>

                      <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                          <h5 className='c-label'>Email Subject</h5>
                          <textarea
                            placeholder='Email Subject'
                            className='c-text-field-4'
                            {...methods.register('emailSubject')}
                            style={{
                              width: '100%',
                              minHeight: '100px',
                              padding: '8px',
                              borderRadius: '8px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                            }}
                            readOnly={(documentSettingId && !open) ? true : false}
                          />
                          <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.emailSubject?.message ?? ""}</p>
                        </Grid>

                        <Grid item xs={12}>
                          <h5 className='c-label'>Email Text</h5>
                          <textarea
                            placeholder='Email Text'
                            className='c-text-field-4'
                            {...methods.register('emailText')}
                            style={{
                              width: '100%',
                              minHeight: '100px',
                              padding: '8px',
                              borderRadius: '8px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                            }}
                            readOnly={(documentSettingId && !open) ? true : false}
                          />
                          <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.emailText?.message ?? ""}</p>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Additional Settings Section - Right Side */}
                    <Grid item xs={12} md={6}>
                      <Divider style={{ marginRight: -8 }}>
                        <Chip label="Additional Settings" size="small" />
                      </Divider>

                      <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                          <h5 className='c-label'>Terms & Conditions</h5>
                          <textarea
                            placeholder='Terms & Conditions'
                            className='c-text-field-4'
                            {...methods.register('termsAndConditions')}
                            style={{
                              width: '100%',
                              minHeight: '100px',
                              padding: '8px',
                              borderRadius: '8px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                            }}
                            readOnly={(documentSettingId && !open) ? true : false}
                          />
                          <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.termsAndConditions?.message ?? ""}</p>
                        </Grid>

                        <Grid item xs={12}>
                          <h5 className='c-label'>Notes/Disclaimer</h5>
                          <textarea
                            placeholder='Notes/Disclaimer'
                            className='c-text-field-4'
                            {...methods.register('notes')}
                            style={{
                              width: '100%',
                              minHeight: '100px',
                              padding: '8px',
                              borderRadius: '8px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                            }}
                            readOnly={(documentSettingId && !open) ? true : false}
                          />
                          <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{methods.formState.errors.notes?.message ?? ""}</p>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
          </Box>
        )}
      </FormProvider>
    </Dialog>
  );
};

export default DialogDocumentSettingForm;